<script lang="ts" setup>
import axios from 'axios'
import dayjs from 'dayjs'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteLocationRaw } from 'vue-router'

import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { useAuthStore } from '@/stores/auth-store'
import { MyDateRangePickerRef, PaginatedResponse } from '@/types/general'
import { GetDataParameters } from '@/types/table'
import { ReportEmployee } from '@/types/user'

import MyButton from '@/components/my-components/MyButton.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'

interface FetchEmployeeParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const { t } = useI18n()
const authStore = useAuthStore()

const { data, paginationData, refetch, params, loading, error } = usePaginatedTable<
    ReportEmployee,
    FetchEmployeeParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<ReportEmployee>>(
            window.route('dr.company.employees.index', { company: authStore.companyId }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            'start-date': dayjs().subtract(13, 'days').startOf('day').toDate().toISOString(),
            'end-date': dayjs().endOf('day').toDate().toISOString(),
        },
    },
)
const dateRangePickerRef = ref<MyDateRangePickerRef>()

function employeeReports(id: string): RouteLocationRaw {
    return {
        name: 'dr.reports',
        query: {
            filter: {
                // @ts-ignore
                'user-id': id,
                'start-date': params.value.filter['start-date'],
                'end-date': params.value.filter['end-date'],
            },
            tab: 'reports',
        },
    }
}

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}
</script>

<template>
    <MyTable
        table-id="report-employees"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('employees')"
        :has-time-range="!!params.filter['start-date']"
    >
        <template #filters>
            <MyDateRangePicker
                ref="dateRangePickerRef"
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />
        </template>

        <template #actionRow="{ row }">
            <RouterLink :to="employeeReports(row.id)">
                <ActionRowItem v-tooltip="t('showReportsForEmployee')">
                    <mdi:account />
                </ActionRowItem>
            </RouterLink>
        </template>
        <MyTableColumn :name="t('employee')" property="name" />
        <MyTableColumn :name="t('drivingTime')" property="totalDrivingTime" />
        <MyTableColumn :name="t('kmTotal')" property="totalKilometerCount" />
        <MyTableColumn :name="t('status')" property="free" />
        <template #free="{ row }">
            <span v-if="!row.free" v-text="t('enRoute')" />
            <span v-else v-text="t('free')" />
        </template>

        <template #noDataButton>
            <MyButton
                v-if="params.filter['start-date']"
                plain
                scheme="light"
                size="small"
                @click="dateRangePickerRef?.clearDates"
            >
                <mdi:magnify class="mr-2 h-4 w-4" />
                {{ t('searchAllTime') }}
            </MyButton>
        </template>
    </MyTable>
</template>
